/**
 * Info dealer (정보상) config - Sprint 3a.
 * A back-alley NPC in the lead-room screen that sells info for GAME MONEY.
 * Balance principle (PLAN-RENEWAL): game-money info is EXPENSIVE and VAGUE;
 * ad info is CHEAP-in-effort and CERTAIN. So the dealer must never fully
 * out-value an ad hook - its reveals are partial / probabilistic hints.
 *
 * Products:
 *  - whale_hint : names a coin whose whale is likely mid-cycle (medium confidence,
 *                 weaker than the ad-gated wallet tracker which is exact).
 *  - news_tip   : whispers which coin the NEXT news will hit (no direction/strength).
 *  - room_leak  : partially exposes one lead room's reliability band (a range,
 *                 not the exact hidden accuracy).
 * Each purchase yields a one-shot revealed hint (consumed on view / expiry).
 */

export type InfoProductId = 'whale_hint' | 'news_tip' | 'room_leak';

export interface InfoProductConfig {
  id: InfoProductId;
  /** i18n key = 'shop_' + id */
  nameKey: string;
  /** i18n desc key = 'shop_' + id + '_desc' */
  descKey: string;
  /** price = max(minCost, CPS * cpsSeconds) - expensive by design */
  cpsSeconds: number;
  minCost: number;
  /** how long the revealed hint stays valid (ms) */
  hintTtl: number;
}

export const INFO_PRODUCTS: InfoProductConfig[] = [
  { id: 'whale_hint', nameKey: 'shop_whale_hint', descKey: 'shop_whale_hint_desc', cpsSeconds: 900, minCost: 20_000, hintTtl: 120_000 },
  { id: 'news_tip',   nameKey: 'shop_news_tip',   descKey: 'shop_news_tip_desc',   cpsSeconds: 1200, minCost: 30_000, hintTtl: 180_000 },
  { id: 'room_leak',  nameKey: 'shop_room_leak',  descKey: 'shop_room_leak_desc',  cpsSeconds: 1500, minCost: 40_000, hintTtl: 300_000 },
];

export function getInfoProduct(id: string): InfoProductConfig | undefined {
  return INFO_PRODUCTS.find(p => p.id === id);
}

/**
 * Dynamic price for an info product from the player's current CPS.
 * Pure - matches the manipulation cost pattern (max of a floor and CPS-scaled).
 */
export function infoProductCost(product: InfoProductConfig, cps: number): number {
  return Math.max(product.minCost, Math.floor(cps * product.cpsSeconds));
}

/** Reliability band label bucket for the room_leak hint (vague on purpose). */
export function accuracyBand(accuracy: number): 'low' | 'mid' | 'high' {
  if (accuracy < 0.45) return 'low';
  if (accuracy < 0.68) return 'mid';
  return 'high';
}
